/**
 * Tenant Admin — Payment refunds (iter181c).
 *
 * Single-shop online payments that did not become an ordinary paid order: underpaid (no order, refunded in full),
 * overpaid (order placed, difference refunded) and refused / abandoned payments (refunded unless used within the grace
 * period). Each row is one record of ``backend/payment_exceptions.py`` with its refund state; the drawer shows the
 * full history and what the gateway itself holds. A refund is retried here after the automatic attempts ran out, or
 * a record is closed with a note when it was settled outside the app (refunded from the gateway's dashboard, cash).
 */
import { useEffect, useState } from "react";
import { AlertTriangle, Loader2, RefreshCw, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, inr2 } from "../../lib/api";

const KIND_LABEL = { UNDERPAID: "Underpaid", OVERPAID: "Overpaid", ORDER_NOT_PLACED: "Not used for an order" };
const STATUS_META = {
    REFUND_DUE: { label: "Refund due", cls: "bg-amber-100 text-amber-800" },
    REFUND_PENDING: { label: "With the gateway", cls: "bg-sky-100 text-sky-800" },
    REFUND_FAILED: { label: "Refund failed", cls: "bg-red-100 text-red-700" },
    REFUNDED: { label: "Refunded", cls: "bg-emerald-100 text-emerald-700" },
    CLOSED_USED: { label: "Used for an order", cls: "bg-muted text-muted-foreground" },
    CLOSED_ELSEWHERE: { label: "Handled elsewhere", cls: "bg-muted text-muted-foreground" },
    CLOSED_NOT_CAPTURED: { label: "Never captured", cls: "bg-muted text-muted-foreground" },
    RESOLVED: { label: "Closed by hand", cls: "bg-muted text-muted-foreground" },
};
const FILTERS = [
    { key: "OPEN", label: "Open", status: "REFUND_DUE,REFUND_PENDING,REFUND_FAILED" },
    { key: "ATTENTION", label: "Needs attention", attention: true },
    { key: "ALL", label: "All" },
];
const OPEN = ["REFUND_DUE", "REFUND_PENDING", "REFUND_FAILED"];
const rupees = (paise) => inr2((Number(paise) || 0) / 100);
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

function StatusPill({ r }) {
    const m = STATUS_META[r.status] || { label: r.status, cls: "bg-muted text-muted-foreground" };
    return (
        <span className="inline-flex items-center gap-1 flex-wrap">
            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${m.cls}`}>{m.label}</span>
            {r.needs_attention && (
                <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-red-600 text-white inline-flex items-center gap-1">
                    <AlertTriangle size={10} /> Attention
                </span>
            )}
        </span>
    );
}

export default function AdminPaymentRecords() {
    const [filter, setFilter] = useState("OPEN");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(null);          // full record shown in the drawer
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        const f = FILTERS.find((x) => x.key === filter);
        const params = new URLSearchParams();
        if (f.status) params.set("status", f.status);
        if (f.attention) params.set("needs_attention", "true");
        try {
            const { data } = await api.get(`/admin/payment-exceptions${params.toString() ? `?${params}` : ""}`);
            setRows(data || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not load payment records");
            setRows([]);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

    const show = async (id) => {
        setNote("");
        try {
            const { data } = await api.get(`/admin/payment-exceptions/${id}`);
            setOpen(data);
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not open the record"); }
    };
    const act = async (path, body) => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/payment-exceptions/${open.id}/${path}`, body);
            setOpen({ ...data, gateway_refunds: open.gateway_refunds });
            toast.success(path === "retry" ? `Refund: ${STATUS_META[data.status]?.label || data.status}` : "Record closed");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "That did not work");
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="admin-payment-records">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="min-w-0">
                    <h1 className="text-2xl font-bold">Payment refunds</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Online payments that did not become an ordinary paid order — refunded automatically, with every step recorded.
                    </p>
                </div>
                <button data-testid="pr-reload" onClick={load} className="inline-flex items-center gap-2 rounded-xl border border-border px-3 py-2 text-sm font-semibold">
                    <RefreshCw size={14} /> Refresh
                </button>
            </div>

            <div className="mt-4 flex gap-2 flex-wrap">
                {FILTERS.map((f) => (
                    <button key={f.key} data-testid={`pr-filter-${f.key.toLowerCase()}`} onClick={() => setFilter(f.key)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold border ${filter === f.key ? "bg-primary text-primary-foreground border-primary" : "bg-muted text-muted-foreground border-border"}`}>
                        {f.label}
                    </button>
                ))}
            </div>

            <div className="mt-4 bg-card border border-border rounded-2xl overflow-hidden">
                {loading ? (
                    <div className="p-10 flex justify-center"><Loader2 className="animate-spin" /></div>
                ) : rows.length === 0 ? (
                    <p data-testid="pr-empty" className="p-10 text-center text-sm text-muted-foreground">Nothing here — no payment needs a refund.</p>
                ) : (
                    <>
                        <div className="lg:hidden divide-y divide-border">
                            {rows.map((r) => (
                                <button key={r.id} data-testid={`pr-card-${r.id}`} onClick={() => show(r.id)} className="w-full text-left p-4 flex flex-col gap-1.5">
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <span className="font-semibold text-sm">{KIND_LABEL[r.kind] || r.kind}</span>
                                        <StatusPill r={r} />
                                    </div>
                                    <p className="text-xs text-muted-foreground">Refund {rupees(r.refund_paise)} of {rupees(r.paid_paise)} · {when(r.created_at)}</p>
                                    <p className="text-[11px] text-muted-foreground truncate">Payment {r.payment_id}</p>
                                </button>
                            ))}
                        </div>
                        <div className="hidden lg:block overflow-x-auto">
                            <table className="w-full min-w-[900px] text-sm">
                                <thead className="bg-muted/50 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                                    <tr><th className="p-3">Opened</th><th className="p-3">Kind</th><th className="p-3">Status</th><th className="p-3">Paid</th><th className="p-3">Refund</th><th className="p-3">Attempts</th><th className="p-3">Payment</th><th className="p-3" /></tr>
                                </thead>
                                <tbody>
                                    {rows.map((r) => (
                                        <tr key={r.id} data-testid={`pr-row-${r.id}`} className="border-t border-border">
                                            <td className="p-3 whitespace-nowrap">{when(r.created_at)}</td>
                                            <td className="p-3">{KIND_LABEL[r.kind] || r.kind}</td>
                                            <td className="p-3"><StatusPill r={r} /></td>
                                            <td className="p-3">{rupees(r.paid_paise)}</td>
                                            <td className="p-3">{rupees(r.refund_paise)}</td>
                                            <td className="p-3">{r.attempts || 0}</td>
                                            <td className="p-3 font-mono text-xs">{r.payment_id}</td>
                                            <td className="p-3 text-right"><button data-testid={`pr-open-${r.id}`} onClick={() => show(r.id)} className="text-primary font-semibold text-xs">Open</button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>

            {open && (
                <div className="fixed inset-0 z-50 bg-black/60 flex justify-end" onClick={() => setOpen(null)}>
                    <div data-testid="pr-drawer" className="max-h-none h-full w-full max-w-lg bg-card overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">{KIND_LABEL[open.kind] || open.kind}</p>
                                <h2 className="text-lg font-bold">Refund {rupees(open.refund_paise)}</h2>
                                <div className="mt-1"><StatusPill r={open} /></div>
                            </div>
                            <button data-testid="pr-close" onClick={() => setOpen(null)} className="p-2 rounded-lg hover:bg-muted" aria-label="Close"><X size={16} /></button>
                        </div>
                        <dl className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                            <dt className="text-muted-foreground">Paid</dt><dd>{rupees(open.paid_paise)}</dd>
                            <dt className="text-muted-foreground">Order total</dt><dd>{open.due_paise == null ? "—" : rupees(open.due_paise)}</dd>
                            <dt className="text-muted-foreground">Payment</dt><dd className="font-mono text-xs break-all">{open.payment_id}</dd>
                            <dt className="text-muted-foreground">Order</dt><dd className="font-mono text-xs break-all">{open.order_id || "—"}</dd>
                            <dt className="text-muted-foreground">Customer</dt><dd className="font-mono text-xs break-all">{open.customer_id}</dd>
                            <dt className="text-muted-foreground">Why</dt><dd>{open.reason}</dd>
                            {open.last_error && (<><dt className="text-muted-foreground">Last error</dt><dd className="text-red-700 break-words">{open.last_error}</dd></>)}
                            {open.blocked && (<><dt className="text-muted-foreground">Waiting for</dt><dd className="text-amber-800 break-words">{open.blocked}</dd></>)}
                        </dl>

                        <h3 className="mt-5 text-[11px] uppercase tracking-wider font-bold text-muted-foreground">At the gateway</h3>
                        {open.gateway_refunds == null ? (
                            <p data-testid="pr-gateway-unknown" className="text-xs text-muted-foreground mt-1">The gateway could not be asked right now.</p>
                        ) : open.gateway_refunds.length === 0 ? (
                            <p className="text-xs text-muted-foreground mt-1">No refund for this payment yet.</p>
                        ) : (
                            <ul data-testid="pr-gateway" className="mt-1 text-xs space-y-1">
                                {open.gateway_refunds.map((g) => (
                                    <li key={g.id} className="flex justify-between gap-2"><span className="font-mono break-all">{g.id}{g.this_record ? " (this record)" : ""}</span><span>{rupees(g.amount_paise)} · {g.status}</span></li>
                                ))}
                            </ul>
                        )}

                        {OPEN.includes(open.status) && (
                            <div className="mt-5 space-y-3">
                                <button data-testid="pr-retry" disabled={busy} onClick={() => act("retry")}
                                    className="w-full bg-primary text-primary-foreground rounded-xl py-2.5 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                                    {busy ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Try the refund now
                                </button>
                                {open.status !== "REFUND_PENDING" && (
                                    <div className="space-y-2">
                                        <textarea data-testid="pr-note" className="inp w-full" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                                            placeholder="Settled outside the app? Say how (e.g. refunded from the Razorpay dashboard, ref …)" />
                                        <button data-testid="pr-resolve" disabled={busy || note.trim().length < 3} onClick={() => act("resolve", { note: note.trim() })}
                                            className="w-full border border-border rounded-xl py-2.5 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                                            <CheckCircle2 size={16} /> Close this record with the note
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        <h3 className="mt-5 text-[11px] uppercase tracking-wider font-bold text-muted-foreground">History</h3>
                        <ol data-testid="pr-history" className="mt-2 space-y-2">
                            {(open.history || []).map((h, i) => (
                                <li key={i} className="text-xs border-l-2 border-border pl-3">
                                    <p className="font-semibold">{h.event}{h.actor ? ` · ${h.actor}` : ""}</p>
                                    <p className="text-muted-foreground">{when(h.at)}{h.note ? ` — ${h.note}` : ""}{h.error ? ` — ${h.error}` : ""}{h.refund_id ? ` — ${h.refund_id}` : ""}</p>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            )}
        </div>
    );
}
